import { useState, useEffect } from 'react';
import { 
  History, 
  Search, 
  Filter,
  Calendar,
  User,
  Building2,
  Download,
  RefreshCw,
  Eye,
  ChevronRight
} from 'lucide-react';
import Card from '../../components/common/Card';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';

interface AuditLog {
  id: string;
  action_type: string;
  performed_by: string;
  performed_by_email?: string;
  performed_by_name?: string;
  target_user_id: string;
  target_user_email?: string;
  target_user_name?: string;
  organization_id?: string;
  organization_name?: string;
  old_value?: any;
  new_value?: any;
  reason?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

const ACTION_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  user_role_changed: { label: 'Role Changed', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  user_added_to_org: { label: 'Added to Organization', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  user_removed_from_org: { label: 'Removed from Organization', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  user_removed_from_platform: { label: 'Deleted from Platform', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  membership_request_approved: { label: 'Membership Approved', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  membership_request_rejected: { label: 'Membership Rejected', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  user_invited: { label: 'User Invited', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  user_activated: { label: 'User Activated', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  user_deactivated: { label: 'User Deactivated', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
};

export default function UserManagementAuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pageSize] = useState(50);
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, actionTypeFilter, startDate, endDate]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      };
      
      if (actionTypeFilter) params.actionType = actionTypeFilter;
      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) params.endDate = new Date(endDate).toISOString();

      const response = await api.get('/platform/users/audit-logs', { params });
      setLogs(response.data.data || []);
      setTotalLogs(response.data.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Search is done on the client side for now
    setCurrentPage(1);
  };

  const handleExport = () => {
    // Convert logs to CSV
    const headers = ['Date', 'Action', 'Performed By', 'Target User', 'Organization', 'Reason'];
    const rows = filteredLogs.map(log => [
      new Date(log.created_at).toLocaleString(),
      ACTION_TYPE_LABELS[log.action_type]?.label || log.action_type,
      log.performed_by_name || log.performed_by_email || log.performed_by,
      log.target_user_name || log.target_user_email || log.target_user_id,
      log.organization_name || 'N/A',
      log.reason || 'N/A'
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-management-audit-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActionTypeFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  // Client-side search filter
  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.performed_by_name?.toLowerCase().includes(search) ||
      log.performed_by_email?.toLowerCase().includes(search) ||
      log.target_user_name?.toLowerCase().includes(search) ||
      log.target_user_email?.toLowerCase().includes(search) ||
      log.organization_name?.toLowerCase().includes(search) ||
      log.reason?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(totalLogs / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <History className="w-8 h-8 mr-3 text-primary-600" />
            User Management Audit Trail
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Complete history of all user management actions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={fetchLogs}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={filteredLogs.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by user, organization, or reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Action Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Action Type
              </label>
              <select
                value={actionTypeFilter}
                onChange={(e) => {
                  setActionTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">All Actions</option>
                {Object.entries(ACTION_TYPE_LABELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Audit Logs */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No audit logs found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                onClick={() => {
                  setSelectedLog(log);
                  setShowDetailModal(true);
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        ACTION_TYPE_LABELS[log.action_type]?.color || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {ACTION_TYPE_LABELS[log.action_type]?.label || log.action_type}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Performed by: </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {log.performed_by_name || log.performed_by_email || log.performed_by}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Target user: </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {log.target_user_name || log.target_user_email || log.target_user_id}
                        </span>
                      </div>
                      {log.organization_name && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Organization: </span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {log.organization_name}
                          </span>
                        </div>
                      )}
                    </div>

                    {log.reason && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Reason: </span>
                        <span className="text-gray-900 dark:text-white italic">
                          "{log.reason}"
                        </span>
                      </div>
                    )}
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 mt-4">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalLogs)} of {totalLogs} logs
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <Modal onClose={() => { setShowDetailModal(false); setSelectedLog(null); }} panelClassName="max-w-3xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Audit Log Details
                </h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedLog(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Action Type */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Action Type</h3>
                <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                  ACTION_TYPE_LABELS[selectedLog.action_type]?.color || 'bg-gray-100 text-gray-800'
                }`}>
                  {ACTION_TYPE_LABELS[selectedLog.action_type]?.label || selectedLog.action_type}
                </span>
              </div>

              {/* Timestamp */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Timestamp
                </h3>
                <p className="text-gray-900 dark:text-white">
                  {new Date(selectedLog.created_at).toLocaleString()}
                </p>
              </div>

              {/* Performed By */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Performed By
                </h3>
                <div className="text-gray-900 dark:text-white">
                  <p className="font-medium">{selectedLog.performed_by_name || 'Unknown'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedLog.performed_by_email}</p>
                </div>
              </div>

              {/* Target User */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Target User
                </h3>
                <div className="text-gray-900 dark:text-white">
                  <p className="font-medium">{selectedLog.target_user_name || 'Unknown'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedLog.target_user_email}</p>
                </div>
              </div>

              {/* Organization */}
              {selectedLog.organization_name && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Organization
                  </h3>
                  <p className="text-gray-900 dark:text-white">{selectedLog.organization_name}</p>
                </div>
              )}

              {/* Changes */}
              {(selectedLog.old_value || selectedLog.new_value) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Changes</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLog.old_value && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Old Value</p>
                        <pre className="text-sm text-gray-900 dark:text-white overflow-x-auto">
                          {JSON.stringify(selectedLog.old_value, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.new_value && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                        <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">New Value</p>
                        <pre className="text-sm text-gray-900 dark:text-white overflow-x-auto">
                          {JSON.stringify(selectedLog.new_value, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reason */}
              {selectedLog.reason && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Reason</h3>
                  <p className="text-gray-900 dark:text-white italic">"{selectedLog.reason}"</p>
                </div>
              )}

              {/* Technical Details */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Technical Details</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Log ID: </span>
                    <span className="text-gray-900 dark:text-white font-mono">{selectedLog.id}</span>
                  </div>
                  {selectedLog.ip_address && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">IP Address: </span>
                      <span className="text-gray-900 dark:text-white font-mono">{selectedLog.ip_address}</span>
                    </div>
                  )}
                  {selectedLog.user_agent && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">User Agent: </span>
                      <span className="text-gray-900 dark:text-white text-xs">{selectedLog.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedLog(null);
                }}
              >
                Close
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// Made with Bob
